/**
 * Error mapping of the product screens (TO-416 product-admin-ui REQ-010). The
 * product-service envelope is `{ error: { code, message, details? } }`, so the
 * machine code is read from `error.code` (not parsed from the message).
 */

type Translate = (key: string, vars?: Record<string, string | number>) => string

/** Every code the product, category and locale-settings screens translate. */
export const PRODUCT_ERROR_CODES = [
	'PRODUCT_NOT_FOUND',
	'PRODUCT_CATEGORY_NOT_FOUND',
	'PRODUCT_TRANSLATION_NOT_FOUND',
	'PRODUCT_CODE_TAKEN',
	'PRODUCT_CATEGORY_CODE_TAKEN',
	'PRODUCT_CATEGORY_IN_USE',
	'PRODUCT_TRANSITION_NOT_ALLOWED',
	'PRODUCT_STATUS_CONFLICT',
	'PRODUCT_TRANSLATION_REQUIRED',
	'PRODUCT_TRANSLATION_CONFLICT',
	'PRODUCT_CTA_INVALID',
	'PRODUCT_CATEGORY_INVALID',
	'PRODUCT_LOCALE_NOT_ENABLED',
	'PRODUCT_TRANSLATIONS_MISSING',
	'LOCALE_SETTINGS_INVALID',
	'VALIDATION_ERROR',
	'BAD_REQUEST',
] as const

/** Which form sent the request — decides which field a code lands on. */
export type ErrorContext = 'base' | 'translation' | 'category' | 'lifecycle' | 'localeSettings'

const FIELD_OF_CODE: Partial<Record<ErrorContext, Record<string, string>>> = {
	base: {
		PRODUCT_CODE_TAKEN: 'code',
		PRODUCT_CATEGORY_INVALID: 'categoryId',
		PRODUCT_LOCALE_NOT_ENABLED: 'defaultLocale',
		PRODUCT_CTA_INVALID: 'cta',
	},
	category: { PRODUCT_CATEGORY_CODE_TAKEN: 'code' },
}

interface ErrorBody {
	code?: unknown
	details?: { missingLocales?: unknown }
}

const bodyOf = (error: unknown): ErrorBody | null => {
	const data = (error as { response?: { data?: { error?: ErrorBody } } })?.response?.data
	return data && typeof data === 'object' ? (data.error ?? null) : null
}

/** The machine code of an API error, or null. */
export const productErrorCode = (error: unknown): string | null => {
	const code = bodyOf(error)?.code
	return typeof code === 'string' && /^[A-Z_]+$/.test(code) ? code : null
}

/** The locales a refused publish reported missing (`details.missingLocales`). */
export const missingLocalesOf = (error: unknown): string[] => {
	const locales = bodyOf(error)?.details?.missingLocales
	return Array.isArray(locales) ? locales.filter((l): l is string => typeof l === 'string') : []
}

/** HTTP status of an API error, or null. */
export const productErrorStatus = (error: unknown): number | null =>
	(error as { response?: { status?: number } })?.response?.status ?? null

/** The translated message for an API error; unknown codes fall back to the generic message. */
export const productErrorMessage = (error: unknown, t: Translate, exists: (key: string) => boolean): string => {
	const code = productErrorCode(error)
	if (!code || !exists(`errors.${code}`)) return t('errors.generic')
	return code === 'PRODUCT_TRANSLATIONS_MISSING'
		? t(`errors.${code}`, { locales: missingLocalesOf(error).join(', ') })
		: t(`errors.${code}`)
}

/** `{ field, message }` for an API error on the given form; `form` when no field relates. */
export const productFieldError = (
	error: unknown,
	t: Translate,
	exists: (key: string) => boolean,
	context: ErrorContext,
): { field: string; message: string } => {
	const code = productErrorCode(error)
	const field = (code && FIELD_OF_CODE[context]?.[code]) || 'form'
	return { field, message: productErrorMessage(error, t, exists) }
}

/** Translate a map of field → error key into field → message. */
export const translateKeys = (found: Record<string, string>, t: Translate): Record<string, string> =>
	Object.fromEntries(Object.entries(found).map(([field, key]) => [field, t(key)]))
